import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Mail, MessageCircle, Send, RefreshCw, XCircle, CheckCircle, Clock, AlertCircle, Users, ExternalLink, Copy, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INVITE_STATUS = {
  PENDING: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  SENT: { label: 'Inviato', color: 'bg-blue-100 text-blue-800', icon: Send },
  ACCEPTED: { label: 'Accettato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  EXPIRED: { label: 'Scaduto', color: 'bg-gray-100 text-gray-800', icon: Clock },
  REVOKED: { label: 'Revocato', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const DELIVERY_STATUS = {
  SENT: { label: 'Consegnato', color: 'bg-green-100 text-green-800' },
  FAILED: { label: 'Fallito', color: 'bg-red-100 text-red-800' },
  SKIPPED: { label: 'Dev Mode', color: 'bg-orange-100 text-orange-800' },
  MANUAL: { label: 'Manuale', color: 'bg-purple-100 text-purple-800' }
};

const INVITE_CHANNEL = {
  EMAIL: { label: 'Email', icon: Mail },
  WHATSAPP: { label: 'WhatsApp', icon: MessageCircle }
};

export const Invites = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  const [invites, setInvites] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [sendData, setSendData] = useState(null);
  
  const [selectedPerson, setSelectedPerson] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('EMAIL');
  const [selectedPeople, setSelectedPeople] = useState([]);

  const fetchInvites = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setInvites(await res.json());
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  }, [condoId, token]);

  const fetchPeople = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/people`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filter people without linked user
        setPeople(data.filter(p => !p.linked_user_id));
      }
    } catch (error) {
      console.error('Error fetching people:', error);
    }
    setLoading(false);
  }, [condoId, token]);

  useEffect(() => {
    fetchInvites();
    fetchPeople();
  }, [fetchInvites, fetchPeople]);

  // Create invite
  const handleCreate = async () => {
    if (!selectedPerson) {
      toast.error('Seleziona una persona');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          person_id: selectedPerson,
          channel: selectedChannel
        })
      });
      
      if (res.ok) {
        toast.success('Invito creato');
        setIsCreateOpen(false);
        setSelectedPerson('');
        fetchInvites();
        fetchPeople();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore creazione');
    }
  };

  // Bulk create
  const handleBulkCreate = async () => {
    if (selectedPeople.length === 0) {
      toast.error('Seleziona almeno una persona');
      return;
    }
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/invites/bulk?channel=${selectedChannel}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(selectedPeople)
        }
      );
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Creati ${result.created} inviti`);
        if (result.skipped > 0) {
          toast.warning(`${result.skipped} inviti saltati`);
        }
        setIsBulkOpen(false);
        setSelectedPeople([]);
        fetchInvites();
        fetchPeople();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Send invite
  const handleSend = async (inviteId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/invites/${inviteId}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSendData(data);
        setIsSendOpen(true);
        fetchInvites();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore invio');
    }
  };

  // Resend invite
  const handleResend = async (inviteId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/invites/${inviteId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Invito rigenerato');
        fetchInvites();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Revoke invite
  const handleRevoke = async (inviteId) => {
    if (!window.confirm('Revocare questo invito?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/invites/${inviteId}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Invito revocato');
        fetchInvites();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const getPersonName = (personId) => {
    const person = people.find(p => p.id === personId);
    return person?.full_name || personId.slice(0, 8);
  };

  if (userRole !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
            <p className="text-muted-foreground">
              Solo l'amministratore può gestire gli inviti.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Inviti"
        subtitle="Inviti a condòmini e collaboratori"
        actions={
          <>
          <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Users className="mr-2 h-4 w-4" />
                Invito Multiplo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Invito Multiplo</DialogTitle>
                <DialogDescription>
                  Seleziona più persone da invitare contemporaneamente
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Canale di invio</Label>
                  <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EMAIL">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email
                        </div>
                      </SelectItem>
                      <SelectItem value="WHATSAPP">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="h-4 w-4" />
                          WhatsApp
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {people.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">
                      Nessuna persona da invitare
                    </p>
                  ) : (
                    people.map((person) => (
                      <div 
                        key={person.id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50"
                      >
                        <Checkbox
                          checked={selectedPeople.includes(person.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedPeople([...selectedPeople, person.id]);
                            } else {
                              setSelectedPeople(selectedPeople.filter(id => id !== person.id));
                            }
                          }}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{person.full_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {person.email || person.phone || 'Nessun contatto'}
                          </p>
                        </div>
                        {selectedChannel === 'EMAIL' && !person.email && (
                          <Badge variant="outline" className="text-red-600">No email</Badge>
                        )}
                        {selectedChannel === 'WHATSAPP' && !person.phone && (
                          <Badge variant="outline" className="text-red-600">No telefono</Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground">
                  {selectedPeople.length} persone selezionate
                </p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsBulkOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleBulkCreate} disabled={selectedPeople.length === 0}>
                  Crea {selectedPeople.length} Inviti
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Send className="mr-2 h-4 w-4" />
                Nuovo Invito
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuovo Invito</DialogTitle>
                <DialogDescription>
                  Invita un residente a registrarsi
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Persona</Label>
                  <Select value={selectedPerson} onValueChange={setSelectedPerson}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona persona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {people.map((person) => (
                        <SelectItem key={person.id} value={person.id}>
                          {person.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Canale</Label>
                  <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EMAIL">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email
                        </div>
                      </SelectItem>
                      <SelectItem value="WHATSAPP">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="h-4 w-4" />
                          WhatsApp
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleCreate}>Crea Invito</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      {/* Invites Table */}
      <Card>
        <CardHeader>
          <CardTitle>Storico Inviti</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
          ) : invites.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun invito inviato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destinatario</TableHead>
                  <TableHead>Canale</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Consegna</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((invite) => {
                  const statusInfo = INVITE_STATUS[invite.status] || INVITE_STATUS.PENDING;
                  const StatusIcon = statusInfo.icon;
                  const ChannelIcon = INVITE_CHANNEL[invite.channel]?.icon || Mail;
                  const deliveryInfo = invite.delivery_status ? DELIVERY_STATUS[invite.delivery_status] : null;
                  
                  return (
                    <TableRow key={invite.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{getPersonName(invite.person_id)}</p>
                          <p className="text-sm text-muted-foreground">
                            {invite.recipient_email || invite.recipient_phone}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          <ChannelIcon className="h-3 w-3 mr-1" />
                          {INVITE_CHANNEL[invite.channel]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {deliveryInfo ? (
                          <div>
                            <Badge className={deliveryInfo.color}>
                              {deliveryInfo.label}
                            </Badge>
                            {invite.delivery_error && (
                              <p className="text-xs text-red-600 mt-1 max-w-32 truncate" title={invite.delivery_error}>
                                {invite.delivery_error}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(invite.expires_at)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {invite.status === 'PENDING' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSend(invite.id)}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Invia
                          </Button>
                        )}
                        {(invite.status === 'SENT' || invite.delivery_status === 'FAILED') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResend(invite.id)}
                            title="Rinvia invito"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                        {['PENDING', 'SENT'].includes(invite.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevoke(invite.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Send Dialog */}
      <Dialog open={isSendOpen} onOpenChange={setIsSendOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invito Inviato</DialogTitle>
          </DialogHeader>
          
          {sendData && (
            <div className="space-y-4">
              {/* Delivery Status Banner */}
              {sendData.delivery_status && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  sendData.delivery_status === 'SENT' ? 'bg-green-50 text-green-800' :
                  sendData.delivery_status === 'FAILED' ? 'bg-red-50 text-red-800' :
                  sendData.delivery_status === 'SKIPPED' ? 'bg-orange-50 text-orange-800' :
                  'bg-purple-50 text-purple-800'
                }`}>
                  {sendData.delivery_status === 'SENT' && <CheckCircle className="h-5 w-5" />}
                  {sendData.delivery_status === 'FAILED' && <AlertCircle className="h-5 w-5" />}
                  {sendData.delivery_status === 'SKIPPED' && <Clock className="h-5 w-5" />}
                  {sendData.delivery_status === 'MANUAL' && <ExternalLink className="h-5 w-5" />}
                  <div>
                    <p className="font-medium">
                      {sendData.delivery_status === 'SENT' && 'Email inviata con successo!'}
                      {sendData.delivery_status === 'FAILED' && 'Invio email fallito'}
                      {sendData.delivery_status === 'SKIPPED' && 'Modalità sviluppo - Email non inviata'}
                      {sendData.delivery_status === 'MANUAL' && 'Azione manuale richiesta'}
                    </p>
                    {sendData.delivery_error && (
                      <p className="text-sm opacity-80">{sendData.delivery_error}</p>
                    )}
                  </div>
                </div>
              )}

              {sendData.channel === 'EMAIL' ? (
                <>
                  {/* Show email details only for fallback/failed modes */}
                  {sendData.delivery_status !== 'SENT' && sendData.subject && (
                    <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Destinatario</Label>
                        <p className="font-mono">{sendData.recipient}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Oggetto</Label>
                        <p>{sendData.subject}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Messaggio</Label>
                        <pre className="text-sm whitespace-pre-wrap bg-white p-3 rounded border max-h-48 overflow-y-auto">
                          {sendData.body}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {sendData.delivery_status === 'SENT' ? (
                    <div className="text-center py-4">
                      <p className="text-muted-foreground">
                        L'invito è stato inviato a <strong>{sendData.recipient}</strong>
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => copyToClipboard(sendData.invite_link)}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copia Link
                      </Button>
                      <Button 
                        className="flex-1"
                        onClick={() => window.open(`mailto:${sendData.recipient}?subject=${encodeURIComponent(sendData.subject || '')}&body=${encodeURIComponent(sendData.body || '')}`)}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Apri Email
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="bg-green-50 p-4 rounded-lg space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Numero</Label>
                      <p className="font-mono">{sendData.recipient}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Messaggio</Label>
                      <pre className="text-sm whitespace-pre-wrap bg-white p-3 rounded border max-h-48 overflow-y-auto">
                        {sendData.message}
                      </pre>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => window.open(sendData.whatsapp_link, '_blank')}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Apri WhatsApp
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSendOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
